// The machine's problems as its panel lists them (MachineDetail): the ones
// its state stands on, and the ones acknowledged on the node (DECISIONS
// 2026-10-17), each split into what happened and the advice after its
// colon ("UDP receive buffer overruns on :9901 (mediamtx): packets dropped
// … (raise …)").
import type { Event, MachineAck, MachineProblem, MachineStatus } from './api'

export interface ShownProblem {
    key: string // '' from a node before keys: nothing to acknowledge
    what: string
    hint: string
    fault: boolean
}
export interface AckedProblem extends ShownProblem {
    text: string
    ack?: MachineAck // who, when and why (the config's)
}

export function split(text: string) {
    const i = text.indexOf(': ')
    return i > 0 ? { what: text.slice(0, i), hint: text.slice(i + 2) } : { what: text, hint: '' }
}

// every problem now with its key, faults first; an older node sends only
// the texts
function listed(m: MachineStatus): MachineProblem[] {
    if (m.problems) return m.problems
    return [
        ...(m.faults ?? []).map((text) => ({ key: '', severity: 'fault' as const, text })),
        ...(m.hardware ?? []).map((text) => ({ key: '', severity: 'warning' as const, text })),
    ]
}

// open: the problems the state stands on (not acknowledged), or a load
// state's reason when there is none; acked: those acknowledged, with their
// acks, still listed (dimmed) until they lapse
export function machineProblems(m: MachineStatus, acks: MachineAck[] = []) {
    const all = listed(m)
    const open: ShownProblem[] = all
        .filter((p) => !p.acked)
        .map((p) => ({ key: p.key, ...split(p.text), fault: p.severity === 'fault' }))
    if (!open.length && m.state !== 'ok' && m.error) open.push({ key: '', ...split(m.error), fault: false })
    const acked: AckedProblem[] = all
        .filter((p) => p.acked)
        .map((p) => ({
            key: p.key,
            text: p.text,
            ...split(p.text),
            fault: p.severity === 'fault',
            ack: acks.find((a) => a.key === p.key),
        }))
    return { open, acked }
}

// The text of an open alarm in the header's list. A state event's message
// is the text when the state changed, and the machine writes no new one
// while problems join or leave "degraded" (only the state is an event), so
// its alarm says what the machine has now: the first problem not
// acknowledged and how many more. Any other alarm, or a machine whose
// status has nothing open (its state event not in yet), keeps its message.
export function alarmText(e: Pick<Event, 'kind' | 'message'>, m?: MachineStatus) {
    if (e.kind !== 'machine' || !m) return e.message
    const { open } = machineProblems(m)
    if (!open.length) return e.message
    return open[0]!.what + (open.length > 1 ? ` (+${open.length - 1} more)` : '')
}
